import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAdd,
    faArrowLeft,
    faArrowPointer,
    faDrawPolygon,
    faFloppyDisk,
    faMagnifyingGlass,
    faPenRuler,
    faWarehouse,
} from '@fortawesome/free-solid-svg-icons';

import PalletEditor from './PalletEditor';
import {
    createWarehouseMap,
    fetchWarehouseMap,
    fetchWarehouseMapPallet,
    fetchWarehouseRackView,
    fetchWarehouseMaps,
    saveWarehouseMapLayout,
} from '../../api/warehouseMap';
import { useAuthProvider } from '../../providers/AuthProvider';
import './css/WarehouseMap.css';


const DEFAULT_MAP = {
    name: 'Warehouse 1',
    warehouse_code: 'W1',
    floor_name: 'Ground Floor',
    width: 1200,
    height: 800,
    outline: [
        { x: 40, y: 40 },
        { x: 920, y: 40 },
        { x: 920, y: 300 },
        { x: 1160, y: 300 },
        { x: 1160, y: 540 },
        { x: 920, y: 540 },
        { x: 920, y: 760 },
        { x: 40, y: 760 },
    ],
    is_published: false,
};

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
const numberValue = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const DEFAULT_ZONE_COLOR = '#287271';
const DEFAULT_HATCH_COLOR = '#f5c518';
const isHatchedColor = (color) => color === 'no-entry' || color?.startsWith('hatch:');
const baseZoneColor = (color) => {
    if (color === 'no-entry') return DEFAULT_HATCH_COLOR;
    if (color?.startsWith('hatch:')) return color.slice(6);
    return color || DEFAULT_ZONE_COLOR;
};
const darkenColor = (color, amount = 0.25) => {
    const match = /^#([0-9a-f]{6})$/i.exec(color);
    if (!match) return '#1b2021';
    const value = Number.parseInt(match[1], 16);
    const channel = (shift) => Math.round(((value >> shift) & 255) * (1 - amount));
    return `rgb(${channel(16)}, ${channel(8)}, ${channel(0)})`;
};

export default function WarehouseMap({ theme = 'light', customers = [] }) {
    const { permissions } = useAuthProvider();
    const canEditMap = Boolean(permissions?.perm_warehousemap_edit);
    const [maps, setMaps] = useState([]);
    const [selectedMapId, setSelectedMapId] = useState('');
    const [map, setMap] = useState(null);
    const [zones, setZones] = useState([]);
    const [selectedZoneId, setSelectedZoneId] = useState(null);
    const [rackView, setRackView] = useState(null);
    const [rackLoading, setRackLoading] = useState(false);
    const [mode, setMode] = useState('view');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [newMap, setNewMap] = useState(DEFAULT_MAP);
    const [drawingOutline, setDrawingOutline] = useState(false);
    const [openPallet, setOpenPallet] = useState(null);
    const [loadingPalletId, setLoadingPalletId] = useState(null);
    const svgRef = useRef(null);
    const dragRef = useRef(null);
    const rackRequestRef = useRef(0);

    const isDark = theme === 'dark';

    useEffect(() => {
        loadMaps();
        // The initial list load intentionally runs once; later refreshes are explicit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput), 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (!selectedMapId) return;
        if (rackView?.zone?.id && mode === 'view') {
            loadRackView(rackView.zone.id, search);
        } else {
            loadMap(selectedMapId, mode === 'view' ? search : '');
        }
        // Mode changes preserve unsaved designer state; map/search changes reload stock.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMapId, search]);

    const loadMaps = async (preferredMapId = null) => {
        setIsLoading(true);
        setError('');
        try {
            const result = await fetchWarehouseMaps();
            const availableMaps = result.maps || [];
            setMaps(availableMaps);
            setSelectedMapId(preferredMapId || selectedMapId || availableMaps[0]?.id || '');
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setIsLoading(false);
        }
    };

    const loadMap = async (mapId, searchTerm = '') => {
        setIsLoading(true);
        setError('');
        try {
            const result = await fetchWarehouseMap(mapId, searchTerm);
            setMap(result.map);
            setZones(result.zones || []);
            setSelectedZoneId((current) => (
                (result.zones || []).some((zone) => zone.id === current) ? current : null
            ));
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setIsLoading(false);
        }
    };

    const loadRackView = async (zoneId, searchTerm = search) => {
        const requestId = rackRequestRef.current + 1;
        rackRequestRef.current = requestId;
        setRackLoading(true);
        setError('');
        try {
            const result = await fetchWarehouseRackView(selectedMapId, zoneId, searchTerm);
            if (requestId === rackRequestRef.current) setRackView(result);
        } catch (requestError) {
            if (requestId === rackRequestRef.current) setError(requestError.message);
        } finally {
            if (requestId === rackRequestRef.current) setRackLoading(false);
        }
    };

    const openRackView = (zone) => {
        setSelectedZoneId(zone.id);
        loadRackView(zone.id);
    };

    const selectedZone = zones.find((zone) => zone.id === selectedZoneId) || null;
    const allPallets = useMemo(
        () => [
            ...(rackView?.locations || []),
            ...zones.flatMap((zone) => zone.zone_type === 'bulk' ? (zone.pallets || []) : []),
        ].map((pallet) => ({
            ...pallet,
            id: pallet.pallet_id,
            occupied: pallet.item_count > 0,
        })),
        [rackView, zones],
    );

    const createMap = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setError('');
        try {
            const result = await createWarehouseMap(newMap);
            setShowCreate(false);
            setNewMap(DEFAULT_MAP);
            await loadMaps(result.map.id);
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setIsSaving(false);
        }
    };

    const saveLayout = async () => {
        if (!map) return;
        setIsSaving(true);
        setError('');
        try {
            await saveWarehouseMapLayout(map.id, { ...map, zones });
            await loadMap(map.id);
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setIsSaving(false);
        }
    };

    const addZone = () => {
        const existingCodes = new Set(zones.map((zone) => zone.zone_code));
        let sequence = zones.length + 1;
        while (existingCodes.has(`ZONE${sequence}`)) sequence += 1;
        const id = crypto.randomUUID();
        const zone = {
            id,
            zone_code: `ZONE${sequence}`,
            location_prefix: `ZONE${sequence}`,
            label: `New zone ${sequence}`,
            zone_type: 'storage',
            shape_type: 'rect',
            x: Math.round(numberValue(map?.width, 1200) / 2 - 70),
            y: Math.round(numberValue(map?.height, 800) / 2 - 45),
            width: 140,
            height: 90,
            rotation: 0,
            points: [],
            color: '#287271',
            sort_order: zones.length,
            pallets: [],
        };
        setZones((current) => [...current, zone]);
        setSelectedZoneId(id);
    };

    const updateSelectedZone = (changes) => {
        setZones((current) => current.map((zone) => (
            zone.id === selectedZoneId ? { ...zone, ...changes } : zone
        )));
    };

    const deleteSelectedZone = () => {
        setZones((current) => current.filter((zone) => zone.id !== selectedZoneId));
        setSelectedZoneId(null);
    };

    const getSvgPoint = (event) => {
        const svg = svgRef.current;
        if (!svg) return { x: 0, y: 0 };
        const point = svg.createSVGPoint();
        point.x = event.clientX;
        point.y = event.clientY;
        const matrix = svg.getScreenCTM();
        return matrix ? point.matrixTransform(matrix.inverse()) : { x: 0, y: 0 };
    };

    const startZoneDrag = (event, zone, action) => {
        if (mode !== 'design') return;
        event.preventDefault();
        event.stopPropagation();
        const point = getSvgPoint(event);
        dragRef.current = {
            action,
            zoneId: zone.id,
            startX: point.x,
            startY: point.y,
            x: numberValue(zone.x),
            y: numberValue(zone.y),
            width: numberValue(zone.width, 120),
            height: numberValue(zone.height, 80),
        };
        setSelectedZoneId(zone.id);
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const startOutlinePointDrag = (event, point, pointIndex) => {
        if (mode !== 'design') return;
        event.preventDefault();
        event.stopPropagation();
        const pointer = getSvgPoint(event);
        dragRef.current = {
            action: 'outline-point',
            pointIndex,
            startX: pointer.x,
            startY: pointer.y,
            x: numberValue(point.x),
            y: numberValue(point.y),
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const handlePointerMove = (event) => {
        if (!dragRef.current || !map) return;
        const point = getSvgPoint(event);
        const drag = dragRef.current;
        const deltaX = point.x - drag.startX;
        const deltaY = point.y - drag.startY;
        if (drag.action === 'outline-point') {
            setMap((current) => ({
                ...current,
                outline: (current.outline || []).map((outlinePoint, index) => (
                    index === drag.pointIndex
                        ? {
                            x: Math.round(clamp(drag.x + deltaX, 0, numberValue(current.width))),
                            y: Math.round(clamp(drag.y + deltaY, 0, numberValue(current.height))),
                        }
                        : outlinePoint
                )),
            }));
            return;
        }
        setZones((current) => current.map((zone) => {
            if (zone.id !== drag.zoneId) return zone;
            if (drag.action === 'resize') {
                return {
                    ...zone,
                    width: clamp(drag.width + deltaX, 20, numberValue(map.width) - drag.x),
                    height: clamp(drag.height + deltaY, 20, numberValue(map.height) - drag.y),
                };
            }
            return {
                ...zone,
                x: clamp(drag.x + deltaX, 0, numberValue(map.width) - drag.width),
                y: clamp(drag.y + deltaY, 0, numberValue(map.height) - drag.height),
            };
        }));
    };

    const handleCanvasClick = (event) => {
        if (!drawingOutline || !map) return;
        const point = getSvgPoint(event);
        setMap((current) => ({
            ...current,
            outline: [...(current.outline || []), {
                x: Math.round(clamp(point.x, 0, numberValue(current.width))),
                y: Math.round(clamp(point.y, 0, numberValue(current.height))),
            }],
        }));
    };

    const openPalletDetails = async (pallet) => {
        setLoadingPalletId(pallet.pallet_id);
        setError('');
        try {
            const result = await fetchWarehouseMapPallet(pallet.pallet_id);
            setOpenPallet({
                ...result.pallet,
                status: result.pallet.status || 'active',
                occupied: (result.pallet.items || []).length > 0,
            });
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setLoadingPalletId(null);
        }
    };

    const zoneFill = (zone) => {
        if (isHatchedColor(zone.color)) return `url(#warehouse-hatch-${zone.id})`;
        const configuredColor = zone.color || DEFAULT_ZONE_COLOR;
        return configuredColor;
    };

    const zoneFillOpacity = (zone) => {
        const usesStockStatus = zone.zone_type === 'storage' || zone.zone_type === 'bulk';
        if (mode === 'design' || !usesStockStatus) return 1;
        if (search) return numberValue(zone.matching_location_count) > 0 ? 1 : 0.42;
        return numberValue(zone.occupied_location_count) > 0 ? 1 : 0.42;
    };

    const zoneLocationCounts = (zone) => {
        const total = numberValue(zone.location_count);
        return {
            empty: Math.max(total - numberValue(zone.occupied_location_count), 0),
            total,
        };
    };

    if (isLoading && !map && maps.length === 0) {
        return <div className="warehouse-map-state">Loading warehouse maps...</div>;
    }

    return (
        <div className={`warehouse-map ${isDark ? 'warehouse-map--dark' : ''}`}>
            <header className="warehouse-map__header">
                <div>
                    <span className="warehouse-map__eyebrow">OPERATIONS / SPATIAL VIEW</span>
                    <h2>Warehouse Map</h2>
                </div>
                <div className="warehouse-map__header-actions">
                    <select
                        value={selectedMapId}
                        onChange={(event) => {
                            setRackView(null);
                            setSelectedZoneId(null);
                            setSelectedMapId(event.target.value);
                        }}
                        aria-label="Warehouse floor"
                    >
                        {maps.map((mapOption) => (
                            <option key={mapOption.id} value={mapOption.id}>
                                {mapOption.warehouse_code} / {mapOption.floor_name}
                            </option>
                        ))}
                    </select>
                    {canEditMap && (
                        <button className="warehouse-map__command" onClick={() => setShowCreate(true)}>
                            <FontAwesomeIcon icon={faAdd} style={{ width: 16 }} /> Floor
                        </button>
                    )}
                </div>
            </header>

            {error && <div className="warehouse-map__error">{error}</div>}

            {maps.length === 0 && !showCreate ? (
                <div className="warehouse-map__empty">
                    <FontAwesomeIcon icon={faWarehouse} style={{ width: 48 }} />
                    <h3>No floor plans yet</h3>
                    {canEditMap && (
                        <button className="warehouse-map__primary" onClick={() => setShowCreate(true)}>
                            Create first floor plan
                        </button>
                    )}
                </div>
            ) : map ? (
                <>
                    <div className="warehouse-map__toolbar">
                        <div className="warehouse-map__segments" aria-label="Map mode">
                            <button className={mode === 'view' ? 'active' : ''} onClick={() => setMode('view')}>
                                <FontAwesomeIcon icon={faArrowPointer} style={{ width: 16 }} /> View
                            </button>
                            {canEditMap && (
                                <button className={mode === 'design' ? 'active' : ''} onClick={() => {
                                    setRackView(null);
                                    setMode('design');
                                }}>
                                    <FontAwesomeIcon icon={faPenRuler} style={{ width: 16 }} /> Design
                                </button>
                            )}
                        </div>
                        {mode === 'view' ? (
                            <label className="warehouse-map__search">
                                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ width: 16 }} />
                                <input
                                    value={searchInput}
                                    onChange={(event) => setSearchInput(event.target.value)}
                                    placeholder="Product, SKU, batch, barcode or location"
                                />
                            </label>
                        ) : (
                            <div className="warehouse-map__design-actions">
                                <button className="warehouse-map__command" onClick={addZone}>
                                    <FontAwesomeIcon icon={faAdd} style={{ width: 16 }} /> Zone
                                </button>
                                <button
                                    className={`warehouse-map__command ${drawingOutline ? 'active' : ''}`}
                                    onClick={() => {
                                        if (!drawingOutline) setMap((current) => ({ ...current, outline: [] }));
                                        setDrawingOutline((current) => !current);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faDrawPolygon} style={{ width: 16 }} />
                                    {drawingOutline ? 'Finish outline' : 'Redraw outline'}
                                </button>
                                <button className="warehouse-map__primary" onClick={saveLayout} disabled={isSaving}>
                                    <FontAwesomeIcon icon={faFloppyDisk} style={{ width: 16 }} /> {isSaving ? 'Saving...' : 'Save layout'}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="warehouse-map__workspace">
                        <div className="warehouse-map__canvas-wrap">
                            <div className="warehouse-map__canvas-meta">
                                {rackView ? (
                                    <>
                                        <button className="warehouse-map__back" onClick={() => {
                                            setRackView(null);
                                            setSelectedZoneId(null);
                                            loadMap(selectedMapId, search);
                                        }}>
                                            <FontAwesomeIcon icon={faArrowLeft} style={{ width: 16 }} /> Floor plan
                                        </button>
                                        <strong>{rackView.zone?.label || rackView.zone?.zone_code}</strong>
                                        <span>Side elevation</span>
                                    </>
                                ) : (
                                    <>
                                        <strong>{map.name}</strong>
                                        <span>{map.floor_name}</span>
                                        {search && <span>{zones.reduce((total, zone) => total + numberValue(zone.matching_location_count), 0)} matching locations</span>}
                                    </>
                                )}
                            </div>
                            {rackView ? (
                                <RackElevation
                                    data={rackView}
                                    loading={rackLoading}
                                    search={search}
                                    loadingPalletId={loadingPalletId}
                                    openPallet={openPalletDetails}
                                />
                            ) : (
                            <>
                            <svg
                                ref={svgRef}
                                className={`warehouse-map__canvas ${drawingOutline ? 'is-drawing' : ''}`}
                                viewBox={`0 0 ${map.width} ${map.height}`}
                                onClick={handleCanvasClick}
                                onPointerMove={handlePointerMove}
                                onPointerUp={() => { dragRef.current = null; }}
                                onPointerCancel={() => { dragRef.current = null; }}
                            >
                                <defs>
                                    <pattern id="warehouse-grid" width="25" height="25" patternUnits="userSpaceOnUse">
                                        <path d="M 25 0 L 0 0 0 25" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.22" />
                                    </pattern>
                                    {zones.filter((zone) => isHatchedColor(zone.color)).map((zone) => {
                                        const color = baseZoneColor(zone.color);
                                        return (
                                            <pattern key={zone.id} id={`warehouse-hatch-${zone.id}`} width="20" height="20" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                                <rect width="20" height="20" fill={color} />
                                                <rect width="7" height="20" fill={darkenColor(color)} />
                                            </pattern>
                                        );
                                    })}
                                </defs>
                                <rect width={map.width} height={map.height} fill="url(#warehouse-grid)" />
                                {(map.outline || []).length >= 3 && (
                                    <polygon
                                        className="warehouse-map__outline"
                                        points={map.outline.map((point) => `${point.x},${point.y}`).join(' ')}
                                    />
                                )}
                                {mode === 'design' && (map.outline || []).map((point, index) => (
                                    <circle
                                        key={index}
                                        cx={point.x}
                                        cy={point.y}
                                        r="7"
                                        className="warehouse-map__outline-point"
                                        onPointerDown={(event) => startOutlinePointDrag(event, point, index)}
                                        onClick={(event) => event.stopPropagation()}
                                    />
                                ))}
                                {zones.map((zone) => {
                                    const selected = zone.id === selectedZoneId;
                                    const width = numberValue(zone.width, 120);
                                    const height = numberValue(zone.height, 80);
                                    const locationCounts = zoneLocationCounts(zone);
                                    const usesStockStatus = zone.zone_type === 'storage' || zone.zone_type === 'bulk';
                                    return (
                                        <g
                                            key={zone.id}
                                            className={`warehouse-map__zone ${selected ? 'is-selected' : ''}`}
                                            transform={`rotate(${numberValue(zone.rotation)} ${numberValue(zone.x) + width / 2} ${numberValue(zone.y) + height / 2})`}
                                            onPointerDown={(event) => startZoneDrag(event, zone, 'move')}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                if (mode === 'view' && zone.zone_type === 'storage') {
                                                    openRackView(zone);
                                                } else {
                                                    setSelectedZoneId(zone.id);
                                                }
                                            }}
                                        >
                                            <rect
                                                x={zone.x}
                                                y={zone.y}
                                                width={width}
                                                height={height}
                                                fill={zoneFill(zone)}
                                                fillOpacity={zoneFillOpacity(zone)}
                                            />
                                            <text
                                                x={numberValue(zone.x) + width / 2}
                                                y={numberValue(zone.y) + height / 2 + (zone.zone_type === 'structure' ? 0 : -5)}
                                                dominantBaseline={zone.zone_type === 'structure' ? 'middle' : undefined}
                                            >
                                                {zone.label || zone.zone_code}
                                            </text>
                                            {zone.zone_type !== 'structure' && (
                                                <text className="warehouse-map__zone-count" x={numberValue(zone.x) + width / 2} y={numberValue(zone.y) + height / 2 + 20}>
                                                    {usesStockStatus
                                                        ? locationCounts.total
                                                        : zone.zone_type}
                                                </text>
                                            )}
                                            {mode === 'design' && selected && (
                                                <rect
                                                    className="warehouse-map__resize-handle"
                                                    x={numberValue(zone.x) + width - 9}
                                                    y={numberValue(zone.y) + height - 9}
                                                    width="18"
                                                    height="18"
                                                    onPointerDown={(event) => startZoneDrag(event, zone, 'resize')}
                                                />
                                            )}
                                        </g>
                                    );
                                })}
                            </svg>
                            <div className="warehouse-map__legend">
                                <span><i className="occupied" /> Stock present (solid zone colour)</span>
                                <span><i className="empty" /> Empty (faded zone colour)</span>
                                <span><i className="match" /> Search match (solid zone colour)</span>
                            </div>
                            </>
                            )}
                        </div>

                        <aside className="warehouse-map__panel">
                            {rackView ? (
                                <div className="warehouse-map__panel-empty">
                                    <FontAwesomeIcon icon={faArrowPointer} style={{ width: 24 }} />
                                    <p>Select a pallet location to inspect its contents.</p>
                                </div>
                            ) : mode === 'design' ? (
                                <MapInspector
                                    map={map}
                                    setMap={setMap}
                                    zone={selectedZone}
                                    updateZone={updateSelectedZone}
                                    deleteZone={deleteSelectedZone}
                                    drawingOutline={drawingOutline}
                                />
                            ) : selectedZone ? (
                                <ZoneDetails
                                    zone={selectedZone}
                                    search={search}
                                    loadingPalletId={loadingPalletId}
                                    openPallet={openPalletDetails}
                                />
                            ) : (
                                <div className="warehouse-map__panel-empty">
                                    <FontAwesomeIcon icon={faArrowPointer} style={{ width: 24 }} />
                                    <p>Select a zone to inspect its pallet locations.</p>
                                </div>
                            )}
                        </aside>
                    </div>
                </>
            ) : null}

            {canEditMap && showCreate && (
                <div className="warehouse-map__modal" role="dialog" aria-modal="true">
                    <form className="warehouse-map__modal-body" onSubmit={createMap}>
                        <h3>Create warehouse floor</h3>
                        <label>Name<input required value={newMap.name} onChange={(event) => setNewMap({ ...newMap, name: event.target.value })} /></label>
                        <label>Warehouse code<input required value={newMap.warehouse_code} onChange={(event) => setNewMap({ ...newMap, warehouse_code: event.target.value.toUpperCase() })} /></label>
                        <label>Floor name<input required value={newMap.floor_name} onChange={(event) => setNewMap({ ...newMap, floor_name: event.target.value })} /></label>
                        <div className="warehouse-map__modal-actions">
                            <button type="button" className="warehouse-map__command" onClick={() => setShowCreate(false)}>Cancel</button>
                            <button type="submit" className="warehouse-map__primary" disabled={isSaving}>{isSaving ? 'Creating...' : 'Create floor'}</button>
                        </div>
                    </form>
                </div>
            )}

            {openPallet && (
                <PalletEditor
                    pallet={openPallet}
                    allCustomers={customers}
                    allPallets={allPallets}
                    color={{
                        bg: isDark ? '#11191b' : '#f6f8f7',
                        panel: isDark ? '#1d292c' : '#ffffff',
                        fg: isDark ? '#f5f7f6' : '#172023',
                        border: isDark ? '#526064' : '#b8c3c2',
                        headerBg: isDark ? '#263538' : '#e8eeed',
                        highlight: '#f5b700',
                    }}
                    canEdit={false}
                    readOnly
                    onClose={() => setOpenPallet(null)}
                    onSave={() => setOpenPallet(null)}
                />
            )}
        </div>
    );
}

function RackElevation({ data, loading, search, loadingPalletId, openPallet }) {
    const prefix = String(data.zone?.location_prefix || '').toUpperCase();
    const parsedLocations = (data.locations || []).flatMap((location) => {
        const code = String(location.pallet_code || '').toUpperCase();
        const match = code.startsWith(prefix) ? code.slice(prefix.length).match(/^(\d{3})([A-Z])$/) : null;
        return match ? [{ ...location, locationNumber: match[1], level: match[2] }] : [];
    });
    const locationNumbers = [...new Set(parsedLocations.map((location) => location.locationNumber))]
        .sort((left, right) => Number(left) - Number(right));
    if (data.zone?.location_start_side === 'right') locationNumbers.reverse();
    const levels = [...new Set(parsedLocations.map((location) => location.level))]
        .sort((left, right) => right.localeCompare(left));
    const locationBySlot = new Map(parsedLocations.map((location) => [
        `${location.locationNumber}:${location.level}`,
        location,
    ]));

    if (loading && !parsedLocations.length) {
        return <div className="warehouse-map__rack-state">Loading rack locations...</div>;
    }
    if (!parsedLocations.length) {
        return (
            <div className="warehouse-map__rack-state">
                {search ? 'No rack locations match this search.' : `No locations match ${prefix}000A format.`}
            </div>
        );
    }

    return (
        <div className={`warehouse-map__rack ${loading ? 'is-loading' : ''}`}>
            <div
                className="warehouse-map__rack-grid"
                style={{ '--rack-columns': locationNumbers.length }}
            >
                <span className="warehouse-map__rack-corner">Level</span>
                {locationNumbers.map((locationNumber) => (
                    <strong key={locationNumber} className="warehouse-map__rack-heading">
                        {locationNumber}
                    </strong>
                ))}
                {levels.flatMap((level) => [
                    <strong key={`level-${level}`} className="warehouse-map__rack-level">{level}</strong>,
                    ...locationNumbers.map((locationNumber) => {
                        const location = locationBySlot.get(`${locationNumber}:${level}`);
                        return location ? (
                            <button
                                key={`${locationNumber}-${level}`}
                                className={`warehouse-map__rack-slot ${location.matches_search ? 'is-match' : ''} ${location.item_count > 0 ? 'is-occupied' : ''}`}
                                onClick={() => openPallet(location)}
                                disabled={loadingPalletId === location.pallet_id}
                                title={`${location.pallet_code}: ${location.item_count} item lines, ${location.total_quantity} units`}
                            >
                                <span>{location.pallet_code}</span>
                                <small>{location.item_count > 0 ? `${location.total_quantity} units` : 'Empty'}</small>
                            </button>
                        ) : (
                            <span key={`${locationNumber}-${level}`} className="warehouse-map__rack-slot is-unassigned" />
                        );
                    }),
                ])}
            </div>
        </div>
    );
}

function MapInspector({ map, setMap, zone, updateZone, deleteZone, drawingOutline }) {
    return (
        <div className="warehouse-map__inspector">
            <h3>Floor settings</h3>
            <label>Name<input value={map.name} onChange={(event) => setMap({ ...map, name: event.target.value })} /></label>
            <label>Warehouse code<input value={map.warehouse_code} onChange={(event) => setMap({ ...map, warehouse_code: event.target.value.toUpperCase() })} /></label>
            <label>Floor<input value={map.floor_name} onChange={(event) => setMap({ ...map, floor_name: event.target.value })} /></label>
            <div className="warehouse-map__field-row">
                <label>Width<input type="number" min="400" max="5000" value={map.width} onChange={(event) => setMap({ ...map, width: numberValue(event.target.value, 1200) })} /></label>
                <label>Height<input type="number" min="300" max="5000" value={map.height} onChange={(event) => setMap({ ...map, height: numberValue(event.target.value, 800) })} /></label>
            </div>
            <label className="warehouse-map__checkbox"><input type="checkbox" checked={map.is_published} onChange={(event) => setMap({ ...map, is_published: event.target.checked })} /> Published</label>
            {drawingOutline && <p className="warehouse-map__hint">Click around the canvas to trace the building outline, then choose Finish outline.</p>}

            {zone && (
                <>
                    <hr />
                    <h3>Selected zone</h3>
                    <label>Zone code<input value={zone.zone_code} onChange={(event) => updateZone({ zone_code: event.target.value.toUpperCase() })} /></label>
                    <label>Location prefix<input value={zone.location_prefix} onChange={(event) => updateZone({ location_prefix: event.target.value.toUpperCase() })} /></label>
                    <label>Label<input value={zone.label} onChange={(event) => updateZone({ label: event.target.value })} /></label>
                    <label>Type
                        <select value={zone.zone_type} onChange={(event) => updateZone({ zone_type: event.target.value })}>
                            <option value="storage">Racking</option>
                            <option value="bulk">Bulk storage</option>
                            <option value="aisle">Aisle</option>
                            <option value="walkway">Walkway</option>
                            <option value="structure">Structure</option>
                        </select>
                    </label>
                    {zone.zone_type === 'storage' && (
                        <label>Location 001 starts
                            <select
                                value={zone.location_start_side || 'left'}
                                onChange={(event) => updateZone({ location_start_side: event.target.value })}
                            >
                                <option value="left">Left</option>
                                <option value="right">Right</option>
                            </select>
                        </label>
                    )}
                    <label>Fill style
                        <select
                            value={isHatchedColor(zone.color) ? 'hatch' : 'solid'}
                            onChange={(event) => updateZone({
                                color: event.target.value === 'hatch'
                                    ? `hatch:${baseZoneColor(zone.color)}`
                                    : baseZoneColor(zone.color),
                            })}
                        >
                            <option value="solid">Solid colour</option>
                            <option value="hatch">Diagonal hatch</option>
                        </select>
                    </label>
                    <label>Zone colour
                        <input
                            type="color"
                            value={baseZoneColor(zone.color)}
                            onChange={(event) => updateZone({
                                color: isHatchedColor(zone.color) ? `hatch:${event.target.value}` : event.target.value,
                            })}
                        />
                    </label>
                    <div className="warehouse-map__field-row">
                        <label>X<input type="number" value={Math.round(numberValue(zone.x))} onChange={(event) => updateZone({ x: numberValue(event.target.value) })} /></label>
                        <label>Y<input type="number" value={Math.round(numberValue(zone.y))} onChange={(event) => updateZone({ y: numberValue(event.target.value) })} /></label>
                    </div>
                    <div className="warehouse-map__field-row">
                        <label>Width<input type="number" min="20" value={Math.round(numberValue(zone.width))} onChange={(event) => updateZone({ width: numberValue(event.target.value, 20) })} /></label>
                        <label>Height<input type="number" min="20" value={Math.round(numberValue(zone.height))} onChange={(event) => updateZone({ height: numberValue(event.target.value, 20) })} /></label>
                    </div>
                    <label>Rotation<input type="number" min="-180" max="180" value={zone.rotation} onChange={(event) => updateZone({ rotation: numberValue(event.target.value) })} /></label>
                    <button className="warehouse-map__danger" onClick={deleteZone}>Delete zone</button>
                </>
            )}
        </div>
    );
}

function ZoneDetails({ zone, search, loadingPalletId, openPallet }) {
    const pallets = zone.pallets || [];
    const locationCount = numberValue(zone.location_count);
    const occupiedCount = numberValue(zone.occupied_location_count);
    const matchingCount = numberValue(zone.matching_location_count);
    const emptyLocations = Math.max(locationCount - occupiedCount, 0);
    const usesStockStatus = zone.zone_type === 'storage' || zone.zone_type === 'bulk';
    const zoneTypeLabels = {
        aisle: 'aisle',
        walkway: 'walkway',
        structure: 'structure',
    };
    return (
        <div className="warehouse-map__zone-details">
            <span className="warehouse-map__eyebrow">{zone.zone_type}</span>
            <h3>{zone.label}</h3>
            {usesStockStatus ? (
                <>
                    <p>{zone.zone_code} maps locations beginning with <strong>{zone.location_prefix}</strong>.</p>
                    <div className="warehouse-map__occupancy-summary">
                        {search ? (
                            <>
                                <strong>{matchingCount}</strong>
                                <span>{matchingCount === 1 ? 'matching location' : 'matching locations'}</span>
                            </>
                        ) : (
                            <>
                                <strong>{emptyLocations} / {locationCount}</strong>
                                <span>empty locations</span>
                            </>
                        )}
                    </div>
                    {(zone.zone_type === 'storage' || pallets.length === 0) && (
                        <div className="warehouse-map__no-locations">
                            {zone.zone_type === 'storage'
                                ? 'Select this racking zone on the floor plan to open its side elevation.'
                                : 'No matching pallet locations.'}
                        </div>
                    )}
                    {zone.zone_type === 'bulk' && pallets.length > 0 && (
                        <div className="warehouse-map__location-list">
                            {pallets.map((pallet) => (
                                <button
                                    key={pallet.pallet_id}
                                    onClick={() => openPallet(pallet)}
                                    disabled={loadingPalletId === pallet.pallet_id}
                                >
                                    <span>
                                        <strong>{pallet.pallet_code}</strong>
                                        <small>{pallet.item_count} products / {pallet.total_quantity} units</small>
                                    </span>
                                    <i className={pallet.item_count > 0 ? 'occupied' : 'empty'} />
                                </button>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <div className="warehouse-map__no-locations">
                    This {zoneTypeLabels[zone.zone_type] || 'non-storage area'} does not contain pallet locations.
                </div>
            )}
        </div>
    );
}
